import { useState } from "react";

import type { CampaignConflictItem } from "@/@types/campaign-conflict";

import { mergePendingRemoval, type PendingRemoval } from "../../utils/conflict-resolution";
import { invalidatesConflicts } from "../../utils/pro014-payload";
import { CONFLICT_FILTER, PRO014_STEP, PRO014_TOTAL_STEPS } from "../constants";
import type { ConflictFilter, Pro014CampaignData, Pro014WizardState } from "../types";

const clampStep = (step: number): number =>
  Math.min(PRO014_TOTAL_STEPS, Math.max(PRO014_STEP.INFO, step));

export function usePro014WizardState(initialData: Pro014CampaignData): Pro014WizardState {
  const [data, setData] = useState<Pro014CampaignData>(initialData);
  const [currentStep, setCurrentStep] = useState<number>(PRO014_STEP.INFO);
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [pendingRemovals, setPendingRemovals] = useState<PendingRemoval[]>([]);
  const [conflictFilter, setConflictFilter] = useState<ConflictFilter>(CONFLICT_FILTER.ALL);

  const updateData = (changes: Partial<Pro014CampaignData>): void => {
    setData((previous: Pro014CampaignData) => ({ ...previous, ...changes }));
    if (!invalidatesConflicts(changes)) return;
    setConflicts([]);
    setPendingRemovals([]);
  };

  const discardItem = (itemCode: string): void =>
    setData((previous: Pro014CampaignData) => ({
      ...previous,
      items: previous.items.filter((item) => item.sku !== itemCode),
    }));

  const stageRemoval = (removal: PendingRemoval): void =>
    setPendingRemovals((previous: PendingRemoval[]) => mergePendingRemoval(previous, removal));

  const unstageRemoval = (itemCode: string): void =>
    setPendingRemovals((previous: PendingRemoval[]) =>
      previous.filter((pending: PendingRemoval) => pending.itemCode !== itemCode),
    );

  const dropPendingRemovals = (itemCodes: string[]): PendingRemoval[] => {
    const codes: Set<string> = new Set(itemCodes);
    const dropped: PendingRemoval[] = pendingRemovals.filter((pending) => codes.has(pending.itemCode));
    setPendingRemovals((previous: PendingRemoval[]) =>
      previous.filter((pending: PendingRemoval) => !codes.has(pending.itemCode)),
    );
    return dropped;
  };

  return {
    data,
    currentStep,
    conflicts,
    pendingRemovals,
    conflictFilter,
    updateData,
    discardItem,
    setConflicts,
    setConflictFilter,
    stageRemoval,
    unstageRemoval,
    dropPendingRemovals,
    clearPendingRemovals: () => setPendingRemovals([]),
    goNext: () => setCurrentStep((step: number) => clampStep(step + 1)),
    goBack: () => setCurrentStep((step: number) => clampStep(step - 1)),
    goTo: (step: number) => setCurrentStep(clampStep(step)),
  };
}
